// Location and Industry API Service
// Using REST Countries API and other free APIs

export interface Country {
  name: string;
  alpha2Code: string;
  alpha3Code: string;
  capital?: string;
  region: string;
  subregion?: string;
  population: number;
  currencies?: Array<{
    code: string;
    name: string;
    symbol: string;
  }>;
  languages?: Array<{
    iso639_1: string;
    iso639_2: string;
    name: string;
    nativeName: string;
  }>;
  flag: string;
}

export interface City {
  name: string;
  country: string;
  state?: string;
  population?: number;
  latitude?: number;
  longitude?: number;
}

export interface Industry {
  id: string;
  name: string;
  category: string;
  description?: string;
}

// Cache for API responses
const cache = {
  countries: new Map<string, Country[]>(),
  cities: new Map<string, City[]>(),
  industries: new Map<string, Industry[]>(),
  lastFetch: new Map<string, number>()
};

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Check if cache is valid
const isCacheValid = (key: string): boolean => {
  const lastFetch = cache.lastFetch.get(key);
  return lastFetch ? (Date.now() - lastFetch) < CACHE_DURATION : false;
};

// REST Countries API
export const getCountries = async (): Promise<Country[]> => {
  const cacheKey = 'countries';
  
  if (cache.countries.has(cacheKey) && isCacheValid(cacheKey)) {
    return cache.countries.get(cacheKey) || [];
  }

  try {
    const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,cca3,capital,region,subregion,population,currencies,languages,flags');
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const countries: Country[] = data.map((country: any) => ({
      name: country.name.common,
      alpha2Code: country.cca2,
      alpha3Code: country.cca3,
      capital: country.capital?.[0],
      region: country.region,
      subregion: country.subregion,
      population: country.population,
      currencies: country.currencies ? Object.entries(country.currencies).map(([code, curr]: [string, any]) => ({
        code,
        name: curr.name,
        symbol: curr.symbol
      })) : [],
      languages: country.languages ? Object.entries(country.languages).map(([code, lang]: [string, any]) => ({
        iso639_1: code,
        iso639_2: code,
        name: lang,
        nativeName: lang
      })) : [],
      flag: country.flags.svg
    }));

    // Sort countries by name
    countries.sort((a, b) => a.name.localeCompare(b.name));
    
    cache.countries.set(cacheKey, countries);
    cache.lastFetch.set(cacheKey, Date.now());
    
    return countries;
  } catch (error) {
    console.error('Error fetching countries:', error);
    // Return a fallback list of major countries
    return [
      { name: 'United States', alpha2Code: 'US', alpha3Code: 'USA', capital: 'Washington, D.C.', region: 'Americas', population: 331002651, flag: '' },
      { name: 'United Kingdom', alpha2Code: 'GB', alpha3Code: 'GBR', capital: 'London', region: 'Europe', population: 67886011, flag: '' },
      { name: 'Germany', alpha2Code: 'DE', alpha3Code: 'DEU', capital: 'Berlin', region: 'Europe', population: 83190556, flag: '' },
      { name: 'France', alpha2Code: 'FR', alpha3Code: 'FRA', capital: 'Paris', region: 'Europe', population: 65273511, flag: '' },
      { name: 'Canada', alpha2Code: 'CA', alpha3Code: 'CAN', capital: 'Ottawa', region: 'Americas', population: 37742154, flag: '' },
      { name: 'Australia', alpha2Code: 'AU', alpha3Code: 'AUS', capital: 'Canberra', region: 'Oceania', population: 25499884, flag: '' },
      { name: 'Japan', alpha2Code: 'JP', alpha3Code: 'JPN', capital: 'Tokyo', region: 'Asia', population: 125836021, flag: '' },
      { name: 'India', alpha2Code: 'IN', alpha3Code: 'IND', capital: 'New Delhi', region: 'Asia', population: 1380004385, flag: '' },
      { name: 'China', alpha2Code: 'CN', alpha3Code: 'CHN', capital: 'Beijing', region: 'Asia', population: 1439323776, flag: '' },
      { name: 'Brazil', alpha2Code: 'BR', alpha3Code: 'BRA', capital: 'Brasília', region: 'Americas', population: 212559417, flag: '' }
    ];
  }
};

// Get cities for a specific country using comprehensive city database
export const getCitiesForCountry = async (countryCode: string): Promise<City[]> => {
  const cacheKey = `cities_${countryCode}`;
  
  // Clear cache for now to ensure "Other" option is always available
  // if (cache.cities.has(cacheKey) && isCacheValid(cacheKey)) {
  //   return cache.cities.get(cacheKey) || [];
  // }

  try {
    // Comprehensive city database for all major countries
    const citiesDatabase: { [key: string]: City[] } = {
      'US': [
        { name: 'New York', country: 'United States', state: 'New York', population: 8336817 },
        { name: 'Los Angeles', country: 'United States', state: 'California', population: 3979576 },
        { name: 'Chicago', country: 'United States', state: 'Illinois', population: 2693976 },
        { name: 'Houston', country: 'United States', state: 'Texas', population: 2320268 },
        { name: 'Phoenix', country: 'United States', state: 'Arizona', population: 1680992 },
        { name: 'Philadelphia', country: 'United States', state: 'Pennsylvania', population: 1603797 },
        { name: 'San Antonio', country: 'United States', state: 'Texas', population: 1547253 },
        { name: 'San Diego', country: 'United States', state: 'California', population: 1423851 },
        { name: 'Dallas', country: 'United States', state: 'Texas', population: 1343573 },
        { name: 'San Jose', country: 'United States', state: 'California', population: 1030119 },
        { name: 'Austin', country: 'United States', state: 'Texas', population: 978908 },
        { name: 'Jacksonville', country: 'United States', state: 'Florida', population: 949611 },
        { name: 'Fort Worth', country: 'United States', state: 'Texas', population: 918915 },
        { name: 'Columbus', country: 'United States', state: 'Ohio', population: 898553 },
        { name: 'Charlotte', country: 'United States', state: 'North Carolina', population: 885708 }
      ],
      'IN': [
        { name: 'Mumbai', country: 'India', state: 'Maharashtra', population: 20411274 },
        { name: 'Delhi', country: 'India', state: 'Delhi', population: 16787941 },
        { name: 'Bangalore', country: 'India', state: 'Karnataka', population: 12345678 },
        { name: 'Hyderabad', country: 'India', state: 'Telangana', population: 10468603 },
        { name: 'Chennai', country: 'India', state: 'Tamil Nadu', population: 7088000 },
        { name: 'Kolkata', country: 'India', state: 'West Bengal', population: 14974073 },
        { name: 'Pune', country: 'India', state: 'Maharashtra', population: 6500000 },
        { name: 'Ahmedabad', country: 'India', state: 'Gujarat', population: 5570585 },
        { name: 'Jaipur', country: 'India', state: 'Rajasthan', population: 3073350 },
        { name: 'Surat', country: 'India', state: 'Gujarat', population: 4467797 },
        { name: 'Lucknow', country: 'India', state: 'Uttar Pradesh', population: 3382000 },
        { name: 'Kanpur', country: 'India', state: 'Uttar Pradesh', population: 3108000 },
        { name: 'Nagpur', country: 'India', state: 'Maharashtra', population: 2954000 },
        { name: 'Indore', country: 'India', state: 'Madhya Pradesh', population: 2166000 },
        { name: 'Thane', country: 'India', state: 'Maharashtra', population: 1841000 }
      ],
      'SG': [
        { name: 'Singapore', country: 'Singapore', population: 5703600 },
        { name: 'Jurong West', country: 'Singapore', population: 262730 },
        { name: 'Woodlands', country: 'Singapore', population: 254730 },
        { name: 'Tampines', country: 'Singapore', population: 251880 },
        { name: 'Sengkang', country: 'Singapore', population: 249370 },
        { name: 'Hougang', country: 'Singapore', population: 226240 },
        { name: 'Yishun', country: 'Singapore', population: 220430 },
        { name: 'Choa Chu Kang', country: 'Singapore', population: 190890 },
        { name: 'Bukit Merah', country: 'Singapore', population: 151980 },
        { name: 'Bedok', country: 'Singapore', population: 145940 },
        { name: 'Ang Mo Kio', country: 'Singapore', population: 139380 },
        { name: 'Bukit Batok', country: 'Singapore', population: 139280 },
        { name: 'Pasir Ris', country: 'Singapore', population: 138860 },
        { name: 'Clementi', country: 'Singapore', population: 91520 },
        { name: 'Queenstown', country: 'Singapore', population: 89660 }
      ],
      'GB': [
        { name: 'London', country: 'United Kingdom', population: 8982000 },
        { name: 'Birmingham', country: 'United Kingdom', population: 1141816 },
        { name: 'Leeds', country: 'United Kingdom', population: 789194 },
        { name: 'Glasgow', country: 'United Kingdom', population: 626410 },
        { name: 'Sheffield', country: 'United Kingdom', population: 582506 },
        { name: 'Bradford', country: 'United Kingdom', population: 537173 },
        { name: 'Edinburgh', country: 'United Kingdom', population: 488050 },
        { name: 'Liverpool', country: 'United Kingdom', population: 486100 },
        { name: 'Manchester', country: 'United Kingdom', population: 547627 },
        { name: 'Bristol', country: 'United Kingdom', population: 463400 },
        { name: 'Cardiff', country: 'United Kingdom', population: 447287 },
        { name: 'Coventry', country: 'United Kingdom', population: 345324 },
        { name: 'Leicester', country: 'United Kingdom', population: 329839 },
        { name: 'Nottingham', country: 'United Kingdom', population: 321500 },
        { name: 'Newcastle', country: 'United Kingdom', population: 300196 }
      ],
      'DE': [
        { name: 'Berlin', country: 'Germany', population: 3669491 },
        { name: 'Hamburg', country: 'Germany', population: 1841179 },
        { name: 'Munich', country: 'Germany', population: 1471508 },
        { name: 'Cologne', country: 'Germany', population: 1085664 },
        { name: 'Frankfurt', country: 'Germany', population: 753056 },
        { name: 'Stuttgart', country: 'Germany', population: 634830 },
        { name: 'Düsseldorf', country: 'Germany', population: 619294 },
        { name: 'Dortmund', country: 'Germany', population: 588250 },
        { name: 'Essen', country: 'Germany', population: 582760 },
        { name: 'Leipzig', country: 'Germany', population: 587857 },
        { name: 'Bremen', country: 'Germany', population: 567559 },
        { name: 'Dresden', country: 'Germany', population: 556780 },
        { name: 'Hannover', country: 'Germany', population: 532163 },
        { name: 'Nuremberg', country: 'Germany', population: 518365 },
        { name: 'Duisburg', country: 'Germany', population: 498686 }
      ],
      'FR': [
        { name: 'Paris', country: 'France', population: 2161000 },
        { name: 'Marseille', country: 'France', population: 861635 },
        { name: 'Lyon', country: 'France', population: 513275 },
        { name: 'Toulouse', country: 'France', population: 479553 },
        { name: 'Nice', country: 'France', population: 342522 },
        { name: 'Nantes', country: 'France', population: 306694 },
        { name: 'Strasbourg', country: 'France', population: 280966 },
        { name: 'Montpellier', country: 'France', population: 277639 },
        { name: 'Bordeaux', country: 'France', population: 254436 },
        { name: 'Lille', country: 'France', population: 232440 },
        { name: 'Rennes', country: 'France', population: 215366 },
        { name: 'Reims', country: 'France', population: 182460 },
        { name: 'Saint-Étienne', country: 'France', population: 172023 },
        { name: 'Toulon', country: 'France', population: 171953 },
        { name: 'Le Havre', country: 'France', population: 170147 }
      ],
      'CA': [
        { name: 'Toronto', country: 'Canada', population: 2930000 },
        { name: 'Montreal', country: 'Canada', population: 1704694 },
        { name: 'Vancouver', country: 'Canada', population: 631486 },
        { name: 'Calgary', country: 'Canada', population: 1239220 },
        { name: 'Edmonton', country: 'Canada', population: 932546 },
        { name: 'Ottawa', country: 'Canada', population: 994837 },
        { name: 'Winnipeg', country: 'Canada', population: 705244 },
        { name: 'Quebec City', country: 'Canada', population: 531902 },
        { name: 'Hamilton', country: 'Canada', population: 536917 },
        { name: 'Kitchener', country: 'Canada', population: 233222 },
        { name: 'London', country: 'Canada', population: 383822 },
        { name: 'Victoria', country: 'Canada', population: 367770 },
        { name: 'Halifax', country: 'Canada', population: 403131 },
        { name: 'Windsor', country: 'Canada', population: 217188 },
        { name: 'Saskatoon', country: 'Canada', population: 246376 }
      ],
      'AU': [
        { name: 'Sydney', country: 'Australia', population: 5312163 },
        { name: 'Melbourne', country: 'Australia', population: 5078193 },
        { name: 'Brisbane', country: 'Australia', population: 2487098 },
        { name: 'Perth', country: 'Australia', population: 2085973 },
        { name: 'Adelaide', country: 'Australia', population: 1358820 },
        { name: 'Canberra', country: 'Australia', population: 431500 },
        { name: 'Gold Coast', country: 'Australia', population: 646983 },
        { name: 'Newcastle', country: 'Australia', population: 322278 },
        { name: 'Wollongong', country: 'Australia', population: 302739 },
        { name: 'Hobart', country: 'Australia', population: 247068 },
        { name: 'Geelong', country: 'Australia', population: 233429 },
        { name: 'Townsville', country: 'Australia', population: 180820 },
        { name: 'Cairns', country: 'Australia', population: 153075 },
        { name: 'Toowoomba', country: 'Australia', population: 114024 },
        { name: 'Darwin', country: 'Australia', population: 147255 }
      ],
      'JP': [
        { name: 'Tokyo', country: 'Japan', population: 13929286 },
        { name: 'Yokohama', country: 'Japan', population: 3726167 },
        { name: 'Osaka', country: 'Japan', population: 2668586 },
        { name: 'Nagoya', country: 'Japan', population: 2304034 },
        { name: 'Sapporo', country: 'Japan', population: 1952356 },
        { name: 'Fukuoka', country: 'Japan', population: 1538689 },
        { name: 'Kobe', country: 'Japan', population: 1528478 },
        { name: 'Kyoto', country: 'Japan', population: 1464890 },
        { name: 'Kawasaki', country: 'Japan', population: 1452100 },
        { name: 'Saitama', country: 'Japan', population: 1322983 },
        { name: 'Hiroshima', country: 'Japan', population: 1194034 },
        { name: 'Sendai', country: 'Japan', population: 1082000 },
        { name: 'Chiba', country: 'Japan', population: 975829 },
        { name: 'Kitakyushu', country: 'Japan', population: 961286 },
        { name: 'Sakai', country: 'Japan', population: 833414 }
      ],
      'CN': [
        { name: 'Shanghai', country: 'China', population: 24870895 },
        { name: 'Beijing', country: 'China', population: 21540000 },
        { name: 'Guangzhou', country: 'China', population: 16096000 },
        { name: 'Shenzhen', country: 'China', population: 13438000 },
        { name: 'Chengdu', country: 'China', population: 16335000 },
        { name: 'Tianjin', country: 'China', population: 15620000 },
        { name: 'Chongqing', country: 'China', population: 15410000 },
        { name: 'Nanjing', country: 'China', population: 9314685 },
        { name: 'Wuhan', country: 'China', population: 11081000 },
        { name: 'Xi\'an', country: 'China', population: 12950000 },
        { name: 'Hangzhou', country: 'China', population: 10360000 },
        { name: 'Dongguan', country: 'China', population: 8392000 },
        { name: 'Foshan', country: 'China', population: 7194000 },
        { name: 'Shenyang', country: 'China', population: 8294000 },
        { name: 'Harbin', country: 'China', population: 10636000 }
      ],
      'BR': [
        { name: 'São Paulo', country: 'Brazil', population: 12325232 },
        { name: 'Rio de Janeiro', country: 'Brazil', population: 6747816 },
        { name: 'Brasília', country: 'Brazil', population: 3055149 },
        { name: 'Salvador', country: 'Brazil', population: 2886698 },
        { name: 'Fortaleza', country: 'Brazil', population: 2669342 },
        { name: 'Belo Horizonte', country: 'Brazil', population: 2511551 },
        { name: 'Manaus', country: 'Brazil', population: 2219580 },
        { name: 'Curitiba', country: 'Brazil', population: 1933105 },
        { name: 'Recife', country: 'Brazil', population: 1653461 },
        { name: 'Porto Alegre', country: 'Brazil', population: 1483771 },
        { name: 'Goiânia', country: 'Brazil', population: 1516163 },
        { name: 'Guarulhos', country: 'Brazil', population: 1381925 },
        { name: 'Campinas', country: 'Brazil', population: 1223237 },
        { name: 'Nova Iguaçu', country: 'Brazil', population: 1200000 },
        { name: 'Maceió', country: 'Brazil', population: 1025360 }
      ],
      'MY': [
        { name: 'Kuala Lumpur', country: 'Malaysia', population: 1790000 },
        { name: 'George Town', country: 'Malaysia', population: 708127 },
        { name: 'Ipoh', country: 'Malaysia', population: 657892 },
        { name: 'Shah Alam', country: 'Malaysia', population: 646890 },
        { name: 'Petaling Jaya', country: 'Malaysia', population: 638516 },
        { name: 'Johor Bahru', country: 'Malaysia', population: 497067 },
        { name: 'Malacca City', country: 'Malaysia', population: 455300 },
        { name: 'Alor Setar', country: 'Malaysia', population: 405523 },
        { name: 'Miri', country: 'Malaysia', population: 300543 },
        { name: 'Kuching', country: 'Malaysia', population: 325132 },
        { name: 'Kota Kinabalu', country: 'Malaysia', population: 452058 },
        { name: 'Sandakan', country: 'Malaysia', population: 396290 },
        { name: 'Tawau', country: 'Malaysia', population: 397673 },
        { name: 'Kota Bharu', country: 'Malaysia', population: 314964 },
        { name: 'Kuala Terengganu', country: 'Malaysia', population: 285065 }
      ],
      'TH': [
        { name: 'Bangkok', country: 'Thailand', population: 8280925 },
        { name: 'Chiang Mai', country: 'Thailand', population: 131091 },
        { name: 'Pattaya', country: 'Thailand', population: 119532 },
        { name: 'Phuket', country: 'Thailand', population: 258480 },
        { name: 'Hat Yai', country: 'Thailand', population: 159130 },
        { name: 'Udon Thani', country: 'Thailand', population: 247231 },
        { name: 'Khon Kaen', country: 'Thailand', population: 115928 },
        { name: 'Nakhon Ratchasima', country: 'Thailand', population: 174332 },
        { name: 'Ubon Ratchathani', country: 'Thailand', population: 85455 },
        { name: 'Chiang Rai', country: 'Thailand', population: 200000 },
        { name: 'Nakhon Si Thammarat', country: 'Thailand', population: 105000 },
        { name: 'Songkhla', country: 'Thailand', population: 75000 },
        { name: 'Surat Thani', country: 'Thailand', population: 128179 },
        { name: 'Phitsanulok', country: 'Thailand', population: 66000 },
        { name: 'Maha Sarakham', country: 'Thailand', population: 52000 }
      ],
      'ID': [
        { name: 'Jakarta', country: 'Indonesia', population: 10562088 },
        { name: 'Surabaya', country: 'Indonesia', population: 2961000 },
        { name: 'Bandung', country: 'Indonesia', population: 2645000 },
        { name: 'Medan', country: 'Indonesia', population: 2294000 },
        { name: 'Semarang', country: 'Indonesia', population: 1655000 },
        { name: 'Palembang', country: 'Indonesia', population: 1655000 },
        { name: 'Makassar', country: 'Indonesia', population: 1338000 },
        { name: 'Tangerang', country: 'Indonesia', population: 1798000 },
        { name: 'Depok', country: 'Indonesia', population: 1734000 },
        { name: 'Bekasi', country: 'Indonesia', population: 2381000 },
        { name: 'Yogyakarta', country: 'Indonesia', population: 422732 },
        { name: 'Malang', country: 'Indonesia', population: 843810 },
        { name: 'Denpasar', country: 'Indonesia', population: 897500 },
        { name: 'Manado', country: 'Indonesia', population: 675000 },
        { name: 'Padang', country: 'Indonesia', population: 914970 }
      ],
      'PH': [
        { name: 'Manila', country: 'Philippines', population: 1780148 },
        { name: 'Quezon City', country: 'Philippines', population: 2936116 },
        { name: 'Davao City', country: 'Philippines', population: 1632991 },
        { name: 'Caloocan', country: 'Philippines', population: 1583978 },
        { name: 'Cebu City', country: 'Philippines', population: 922611 },
        { name: 'Zamboanga City', country: 'Philippines', population: 861799 },
        { name: 'Antipolo', country: 'Philippines', population: 887399 },
        { name: 'Pasig', country: 'Philippines', population: 803159 },
        { name: 'Taguig', country: 'Philippines', population: 886722 },
        { name: 'Valenzuela', country: 'Philippines', population: 714978 },
        { name: 'Dasmariñas', country: 'Philippines', population: 703141 },
        { name: 'General Santos', country: 'Philippines', population: 697315 },
        { name: 'Bacolod', country: 'Philippines', population: 600783 },
        { name: 'Iloilo City', country: 'Philippines', population: 447992 },
        { name: 'Baguio', country: 'Philippines', population: 345366 }
      ],
      'KR': [
        { name: 'Seoul', country: 'South Korea', population: 9733509 },
        { name: 'Busan', country: 'South Korea', population: 3391941 },
        { name: 'Incheon', country: 'South Korea', population: 2942827 },
        { name: 'Daegu', country: 'South Korea', population: 2466052 },
        { name: 'Daejeon', country: 'South Korea', population: 1538394 },
        { name: 'Gwangju', country: 'South Korea', population: 1502881 },
        { name: 'Suwon', country: 'South Korea', population: 1234270 },
        { name: 'Ulsan', country: 'South Korea', population: 1166035 },
        { name: 'Changwon', country: 'South Korea', population: 1053551 },
        { name: 'Seongnam', country: 'South Korea', population: 951518 },
        { name: 'Goyang', country: 'South Korea', population: 1064094 },
        { name: 'Bucheon', country: 'South Korea', population: 843794 },
        { name: 'Ansan', country: 'South Korea', population: 747035 },
        { name: 'Jeonju', country: 'South Korea', population: 658172 },
        { name: 'Anyang', country: 'South Korea', population: 585177 }
      ],
      'MX': [
        { name: 'Mexico City', country: 'Mexico', population: 9209944 },
        { name: 'Tijuana', country: 'Mexico', population: 1810645 },
        { name: 'Ecatepec', country: 'Mexico', population: 1643623 },
        { name: 'León', country: 'Mexico', population: 1579803 },
        { name: 'Puebla', country: 'Mexico', population: 1542232 },
        { name: 'Zapopan', country: 'Mexico', population: 1232456 },
        { name: 'Juárez', country: 'Mexico', population: 1321004 },
        { name: 'Guadalajara', country: 'Mexico', population: 1385000 },
        { name: 'Monterrey', country: 'Mexico', population: 1135512 },
        { name: 'Chihuahua', country: 'Mexico', population: 925341 },
        { name: 'Mérida', country: 'Mexico', population: 921772 },
        { name: 'San Luis Potosí', country: 'Mexico', population: 824229 },
        { name: 'Aguascalientes', country: 'Mexico', population: 863893 },
        { name: 'Hermosillo', country: 'Mexico', population: 855563 },
        { name: 'Saltillo', country: 'Mexico', population: 807598 }
      ],
      'AR': [
        { name: 'Buenos Aires', country: 'Argentina', population: 3075646 },
        { name: 'Córdoba', country: 'Argentina', population: 1315878 },
        { name: 'Rosario', country: 'Argentina', population: 948312 },
        { name: 'Mendoza', country: 'Argentina', population: 937154 },
        { name: 'La Plata', country: 'Argentina', population: 643133 },
        { name: 'Tucumán', country: 'Argentina', population: 548866 },
        { name: 'Mar del Plata', country: 'Argentina', population: 593337 },
        { name: 'Salta', country: 'Argentina', population: 520683 },
        { name: 'Santa Fe', country: 'Argentina', population: 405683 },
        { name: 'San Juan', country: 'Argentina', population: 471389 },
        { name: 'Resistencia', country: 'Argentina', population: 397532 },
        { name: 'Santiago del Estero', country: 'Argentina', population: 252192 },
        { name: 'Corrientes', country: 'Argentina', population: 346334 },
        { name: 'Posadas', country: 'Argentina', population: 275988 },
        { name: 'San Salvador de Jujuy', country: 'Argentina', population: 265249 }
      ],
      'ZA': [
        { name: 'Johannesburg', country: 'South Africa', population: 957441 },
        { name: 'Cape Town', country: 'South Africa', population: 433688 },
        { name: 'Durban', country: 'South Africa', population: 595061 },
        { name: 'Pretoria', country: 'South Africa', population: 741651 },
        { name: 'Port Elizabeth', country: 'South Africa', population: 312392 },
        { name: 'Bloemfontein', country: 'South Africa', population: 256185 },
        { name: 'East London', country: 'South Africa', population: 267007 },
        { name: 'Kimberley', country: 'South Africa', population: 225160 },
        { name: 'Polokwane', country: 'South Africa', population: 130028 },
        { name: 'Nelspruit', country: 'South Africa', population: 110159 },
        { name: 'Mthatha', country: 'South Africa', population: 78663 },
        { name: 'Bisho', country: 'South Africa', population: 137287 },
        { name: 'Mahikeng', country: 'South Africa', population: 154519 },
        { name: 'Thohoyandou', country: 'South Africa', population: 69553 },
        { name: 'Tshwane', country: 'South Africa', population: 741651 }
      ]
    };

    const cities = citiesDatabase[countryCode] || [];
    
    // Always add "Other" option to all country city lists
    const citiesWithOther = [
      ...cities,
      { name: 'Other', country: 'Unknown', population: 0 }
    ];
    
    if (cities.length === 0) {
      console.log(`No cities found for country code: ${countryCode}`);
      return [
        { name: 'Other', country: 'Unknown', population: 0 }
      ];
    }
    
    return citiesWithOther;
    
    cache.cities.set(cacheKey, citiesWithOther);
    cache.lastFetch.set(cacheKey, Date.now());
    
    return citiesWithOther;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};

// Get industries from a comprehensive list
export const getIndustries = async (): Promise<Industry[]> => {
  const cacheKey = 'industries';
  
  if (cache.industries.has(cacheKey) && isCacheValid(cacheKey)) {
    return cache.industries.get(cacheKey) || [];
  }

  try {
    // Comprehensive industry list
    const industries: Industry[] = [
      // Technology
      { id: 'tech', name: 'Technology', category: 'Technology' },
      { id: 'software', name: 'Software Development', category: 'Technology' },
      { id: 'ai', name: 'Artificial Intelligence', category: 'Technology' },
      { id: 'cybersecurity', name: 'Cybersecurity', category: 'Technology' },
      { id: 'cloud', name: 'Cloud Computing', category: 'Technology' },
      { id: 'fintech', name: 'Financial Technology', category: 'Technology' },
      { id: 'healthtech', name: 'Health Technology', category: 'Technology' },
      { id: 'edtech', name: 'Education Technology', category: 'Technology' },
      
      // Healthcare
      { id: 'healthcare', name: 'Healthcare', category: 'Healthcare' },
      { id: 'pharmaceuticals', name: 'Pharmaceuticals', category: 'Healthcare' },
      { id: 'biotechnology', name: 'Biotechnology', category: 'Healthcare' },
      { id: 'medical-devices', name: 'Medical Devices', category: 'Healthcare' },
      { id: 'telemedicine', name: 'Telemedicine', category: 'Healthcare' },
      { id: 'health-insurance', name: 'Health Insurance', category: 'Healthcare' },
      
      // Finance
      { id: 'finance', name: 'Finance', category: 'Finance' },
      { id: 'banking', name: 'Banking', category: 'Finance' },
      { id: 'insurance', name: 'Insurance', category: 'Finance' },
      { id: 'investment', name: 'Investment Management', category: 'Finance' },
      { id: 'cryptocurrency', name: 'Cryptocurrency', category: 'Finance' },
      { id: 'real-estate-finance', name: 'Real Estate Finance', category: 'Finance' },
      
      // Manufacturing
      { id: 'manufacturing', name: 'Manufacturing', category: 'Manufacturing' },
      { id: 'automotive', name: 'Automotive', category: 'Manufacturing' },
      { id: 'aerospace', name: 'Aerospace', category: 'Manufacturing' },
      { id: 'electronics', name: 'Electronics', category: 'Manufacturing' },
      { id: 'chemicals', name: 'Chemicals', category: 'Manufacturing' },
      { id: 'textiles', name: 'Textiles', category: 'Manufacturing' },
      
      // Retail & E-commerce
      { id: 'retail', name: 'Retail', category: 'Retail' },
      { id: 'ecommerce', name: 'E-commerce', category: 'Retail' },
      { id: 'fashion', name: 'Fashion & Apparel', category: 'Retail' },
      { id: 'luxury', name: 'Luxury Goods', category: 'Retail' },
      { id: 'consumer-electronics', name: 'Consumer Electronics', category: 'Retail' },
      
      // Energy
      { id: 'energy', name: 'Energy', category: 'Energy' },
      { id: 'oil-gas', name: 'Oil & Gas', category: 'Energy' },
      { id: 'renewable-energy', name: 'Renewable Energy', category: 'Energy' },
      { id: 'nuclear-energy', name: 'Nuclear Energy', category: 'Energy' },
      { id: 'utilities', name: 'Utilities', category: 'Energy' },
      
      // Transportation
      { id: 'transportation', name: 'Transportation', category: 'Transportation' },
      { id: 'logistics', name: 'Logistics', category: 'Transportation' },
      { id: 'aviation', name: 'Aviation', category: 'Transportation' },
      { id: 'railway', name: 'Railway', category: 'Transportation' },
      { id: 'shipping', name: 'Shipping', category: 'Transportation' },
      
      // Media & Entertainment
      { id: 'media', name: 'Media & Entertainment', category: 'Media' },
      { id: 'gaming', name: 'Gaming', category: 'Media' },
      { id: 'streaming', name: 'Streaming Services', category: 'Media' },
      { id: 'publishing', name: 'Publishing', category: 'Media' },
      { id: 'advertising', name: 'Advertising', category: 'Media' },
      
      // Education
      { id: 'education', name: 'Education', category: 'Education' },
      { id: 'higher-education', name: 'Higher Education', category: 'Education' },
      { id: 'k12', name: 'K-12 Education', category: 'Education' },
      { id: 'online-learning', name: 'Online Learning', category: 'Education' },
      { id: 'corporate-training', name: 'Corporate Training', category: 'Education' },
      
      // Government
      { id: 'government', name: 'Government', category: 'Government' },
      { id: 'defense', name: 'Defense', category: 'Government' },
      { id: 'public-sector', name: 'Public Sector', category: 'Government' },
      { id: 'military', name: 'Military', category: 'Government' },
      
      // Other Industries
      { id: 'consulting', name: 'Consulting', category: 'Professional Services' },
      { id: 'legal', name: 'Legal Services', category: 'Professional Services' },
      { id: 'accounting', name: 'Accounting', category: 'Professional Services' },
      { id: 'real-estate', name: 'Real Estate', category: 'Real Estate' },
      { id: 'construction', name: 'Construction', category: 'Construction' },
      { id: 'agriculture', name: 'Agriculture', category: 'Agriculture' },
      { id: 'food-beverage', name: 'Food & Beverage', category: 'Food & Beverage' },
      { id: 'tourism', name: 'Tourism', category: 'Tourism' },
      { id: 'hospitality', name: 'Hospitality', category: 'Tourism' },
      { id: 'sports', name: 'Sports', category: 'Sports' },
      { id: 'nonprofit', name: 'Non-profit', category: 'Non-profit' }
    ];

        // Sort industries by name
    industries.sort((a, b) => a.name.localeCompare(b.name));
    
    cache.industries.set(cacheKey, industries);
    cache.lastFetch.set(cacheKey, Date.now());
    
    return industries;
  } catch (error) {
    console.error('Error fetching industries:', error);
    return [];
  }
};

// Search countries by name
export const searchCountries = async (query: string): Promise<Country[]> => {
  const countries = await getCountries();
  const searchTerm = query.toLowerCase();
  
  return countries.filter(country => 
    country.name.toLowerCase().includes(searchTerm) ||
    country.alpha2Code.toLowerCase().includes(searchTerm) ||
    country.alpha3Code.toLowerCase().includes(searchTerm)
  );
};

// Search cities by name
export const searchCities = async (countryCode: string, query: string): Promise<City[]> => {
  const cities = await getCitiesForCountry(countryCode);
  const searchTerm = query.toLowerCase();
  
  return cities.filter(city => 
    city.name.toLowerCase().includes(searchTerm) ||
    (city.state && city.state.toLowerCase().includes(searchTerm))
  );
};

// Search industries by name
export const searchIndustries = async (query: string): Promise<Industry[]> => {
  const industries = await getIndustries();
  const searchTerm = query.toLowerCase();
  
  return industries.filter(industry => 
    industry.name.toLowerCase().includes(searchTerm) ||
    industry.category.toLowerCase().includes(searchTerm)
  );
}; 